import React, { useEffect, useRef, useState } from 'react';
import { FaHeart, FaPlay } from 'react-icons/fa';
import { formatCount } from '../../format';

const SKELETON_TILES = Array.from({ length: 12 });
const HOVER_PREVIEW_DELAY_MS = 500;

const canHoverPreview = () => window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

const ReelBadge = () => (
    <svg className="ig-tile-badge" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M8 5.14v13.72a1 1 0 0 0 1.52.85l10.9-6.86a1 1 0 0 0 0-1.7L9.52 4.29A1 1 0 0 0 8 5.14Z" />
    </svg>
);

const PostTile = React.memo(({ post, index, onOpen }) => {
    const [previewing, setPreviewing] = useState(false);
    const [playing, setPlaying] = useState(false);
    const hoverTimer = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => () => clearTimeout(hoverTimer.current), []);

    // React assigns `muted` after the element exists, so Chrome's autoplay check sees an unmuted video.
    useEffect(() => {
        const video = videoRef.current;
        if (!previewing || !video) return;
        video.muted = true;
        video.play().catch(() => {});
    }, [previewing]);

    const startPreview = () => {
        if (!canHoverPreview()) return;
        clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setPreviewing(true), HOVER_PREVIEW_DELAY_MS);
    };

    // Unmounting the <video> aborts its download, so a preview only costs bandwidth while hovered.
    const stopPreview = () => {
        clearTimeout(hoverTimer.current);
        setPreviewing(false);
        setPlaying(false);
    };

    const fallBackToOriginal = (e) => {
        const img = e.currentTarget;
        if (img.dataset.fallback) return;
        img.dataset.fallback = 'true';
        img.src = post.thumbnailURL;
    };

    return (
        <button
            type="button"
            className="ig-tile"
            onClick={() => {
                stopPreview();
                onOpen(post);
            }}
            onMouseEnter={startPreview}
            onMouseLeave={stopPreview}
            aria-label={`Open post, ${post.likes} likes, ${post.views} views`}
        >
            <img
                src={post.tileURL || post.thumbnailURL}
                alt=""
                width="640"
                height="360"
                loading={index < 8 ? 'eager' : 'lazy'}
                fetchpriority={index < 4 ? 'high' : 'auto'}
                decoding="async"
                onError={post.tileURL ? fallBackToOriginal : undefined}
            />
            {previewing && (
                <video
                    ref={videoRef}
                    className={`ig-tile-video${playing ? ' is-playing' : ''}`}
                    src={post.videoURL}
                    muted
                    autoPlay
                    loop
                    playsInline
                    preload="auto"
                    onPlaying={() => setPlaying(true)}
                    aria-hidden="true"
                />
            )}
            <ReelBadge />
            <span className={`ig-tile-hover${playing ? ' is-hidden' : ''}`} aria-hidden="true">
                <span><FaHeart /> {formatCount(post.likes)}</span>
                <span><FaPlay /> {formatCount(post.views)}</span>
            </span>
        </button>
    );
});

const PostGrid = ({ posts, loading, onOpen }) => (
    <div className="ig-grid">
        {loading
            ? SKELETON_TILES.map((_, i) => <div key={i} className="ig-tile ig-tile--skeleton" aria-hidden="true" />)
            : posts.map((post, i) => <PostTile key={post.videoId} post={post} index={i} onOpen={onOpen} />)}
    </div>
);

export default React.memo(PostGrid);
